import { Button, ResizePanel, Tooltip, Icon } from '@scale/scaleui';
import {
  ChatAttachment,
  ChatTaskBehaviorLogMetadata,
  TextCollectionPerResponseStepParams,
  ChatTaskContextType,
  UIGenerationMetadata,
  UIGenerationEnforcementMode,
} from '@scale/shared/types/chat';
import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';
import _ from 'lodash';
import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  memo,
  useEffect,
  ReactNode,
} from 'react';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { AttachmentViewer } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AttachmentViewer';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { ChatStepChildIdContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatStepChildIdContext';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useConfigureChatMessageDisplay } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useConfigureChatMessageDisplay';
import UtilitiesWrapper from 'react-scale/components/scalers/tasks/common/UtilitiesWrapper';
import { SimpleTextCollectionCore } from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/SimpleTextCollectionCore';
import { useTemplateVariableStore } from 'react-scale/components/scalers/tasks/textcollection/stores/TemplateVariableStore';
import { AutoCritiqueAlert, AutoCritiqueButton, CardAlert } from '../../../TempComponents';
import { ResponseTemplatingProps } from '../../../Card';
import useAutoCritique from '../../../hooks/useAutoCritique';
import {
  extractVariablesFromTextCollectionResponse,
  useTextCollectionStepValidation,
} from '../../../utils';
import FontSizeSelector from '../FontSizeSelector';
import { StepCard } from '../../../step/StepCard';
import { ChatMessageDisplayConfigButton } from '../../../step/ChatMessageDisplayConfig';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ChatIFrame } from '../../../ChatIFrame';
import { FooterContainer } from '../../../components/editor';
import WordCounter from '../../../../common/WordCounter';
import { ExpandableContent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ui/ExpandableContent';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { useEventBus } from 'react-scale/components/scalers/tasks/ThreadedInteraction/events';
import { ChatHtmlRenderer } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatHtmlRenderer';
import { extractTextFromRegex } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import { FeedbackCard, FeedbackGradeEnum } from './Feedback';
import { isBlankField } from '@scale/shared/textcollection/helpers';

type Props = {
  name: string;
  step: TextCollectionPerResponseStepParams;
  candidateText: string;
  candidatePreviewText?: string;
  candidateId: string;
  feedback?: Array<{
    feedback_result_code: number;
    feedback_message: string;
    suggested_action: string;
  }>;
  setCandidateCompletedState: (candidateId: string, completed: boolean) => void;
  response: TextCollectionResponse;
  onResponseSubmit: (response: TextCollectionResponse) => Promise<void>;
  hasSubmittedResponse: boolean;
  readOnly?: boolean;
  lastPrompt?: string | null;
  lastResponse?: string | null;
  chatTaskBehaviorLogMetadata?: ChatTaskBehaviorLogMetadata;
  childId?: string;
  enableInlineComments?: boolean;
  turnIndex?: number;
  attachments?: ChatAttachment[];
  responseTemplatingProps: ResponseTemplatingProps;
  enableIndividualModelResponseReroll?: boolean;
  previousModelResponseSelectorStepId?: string;
  className?: string;
  reactUIGeneratedUrlWithMetadata?: {
    reactUIGeneratedUrl?: string;
  } & UIGenerationMetadata;
  setReactUIGeneratedUrlWithMetadata?: (
    urlWithMetadata: {
      reactUIGeneratedUrl?: string;
    } & UIGenerationMetadata,
  ) => void;
  savedReactUIGeneratedUrlWithMetadata?: {
    reactUIGeneratedUrl?: string;
  } & UIGenerationMetadata;
};

function useCandidateTextCollectionValidation({
  enableFeedbackDisplay,
  readOnly,
  resultChanged,
  submitCodeError,
  response,
  fields,
  templateVariables,
  isValidEmptyResponse,
  allAttachmentsViewed,
  autoCritiqueIsLoading,
  codeGenerationEnforcementPassed,
  feedback,
}: {
  enableFeedbackDisplay: boolean;
  readOnly?: boolean;
  resultChanged: boolean;
  submitCodeError?: string;
  response: TextCollectionResponse;
  fields: TextCollectionField[];
  templateVariables?: Record<string, string>;
  isValidEmptyResponse?: boolean;
  allAttachmentsViewed?: boolean;
  autoCritiqueIsLoading?: boolean;
  codeGenerationEnforcementPassed?: boolean;
  feedback?: Array<{
    feedback_result_code: number;
    feedback_message: string;
    suggested_action: string;
  }>;
}) {
  // Get base validation from useTextCollectionStepValidation
  const baseValidation = useTextCollectionStepValidation({
    readOnly,
    resultChanged,
    submitCodeError,
    response,
    fields,
    templateVariables,
    isValidEmptyResponse,
    allAttachmentsViewed,
    autoCritiqueIsLoading,
    codeGenerationEnforcementPassed,
  });

  // If base validation fails or feedback display is disabled, return that result
  if (!baseValidation.canSubmit || enableFeedbackDisplay === false) {
    return baseValidation;
  }

  // No feedback validation needed if feedback array is empty
  if (!feedback || feedback.length === 0) {
    return baseValidation;
  }

  // Check if all feedback items have a corresponding grade filled out
  const missingFeedbackGrades = feedback.some((_, index) => {
    const fieldId = `feedback_grade_${index}`;
    const annotation = response?.annotations?.[fieldId];

    // Missing grade field or empty grade value
    if (!annotation || !annotation.response) {
      return true;
    }

    if (isBlankField(annotation)) {
      return true;
    }

    return false;
  });

  if (missingFeedbackGrades) {
    return {
      canSubmit: false,
      submitTooltip: 'Please provide ratings for all feedback items',
    };
  }

  // Check for missing justifications in feedback cards
  const missingJustification = Object.entries(response?.annotations || {}).some(
    ([fieldId, annotation]) => {
      // Only check feedback grade fields
      if (!fieldId.startsWith('feedback_grade_')) {
        return false;
      }

      if (annotation.type === 'category' && annotation.response) {
        // Skip validation if the field is blank or improperly formatted
        if (isBlankField(annotation)) {
          return false;
        }

        const rating = String(annotation.response[0][0]).toLowerCase();

        if (
          [FeedbackGradeEnum.Partial, FeedbackGradeEnum.Unused].includes(
            rating as FeedbackGradeEnum,
          )
        ) {
          // This checks the current feedback grade and if the grade is Partial and Unused, it checks the justification field and returns false if the justification is empty
          const justificationFieldId = `feedback_justification_${fieldId.split('_')[2]}`;
          const justificationAnnotation = response?.annotations?.[justificationFieldId];

          if (!justificationAnnotation || !justificationAnnotation.response) {
            return true;
          }

          if (isBlankField(justificationAnnotation)) {
            return true;
          }

          return false;
        }
      }
      return false;
    },
  );
  return {
    canSubmit: !missingJustification,
    submitTooltip: missingJustification
      ? 'Please provide justification for Partial or Unused ratings'
      : baseValidation.submitTooltip,
  };
}

// eslint-disable-next-line complexity
const CandidateTextCollectionCard = ({
  name,
  step: _step,
  candidateText,
  candidatePreviewText,
  candidateId,
  feedback,
  setCandidateCompletedState,
  response,
  onResponseSubmit,
  hasSubmittedResponse,
  readOnly,
  lastPrompt,
  lastResponse,
  chatTaskBehaviorLogMetadata,
  attachments,
  responseTemplatingProps,
  enableIndividualModelResponseReroll,
  previousModelResponseSelectorStepId,
  reactUIGeneratedUrlWithMetadata,
  setReactUIGeneratedUrlWithMetadata,
  savedReactUIGeneratedUrlWithMetadata,
  className,
}: Props) => {
  const [fontSizeClass, setFontSizeClass] = useState<string>('text-sm');
  const taskContext = useContext(ChatTaskContext);
  const eventBus = useEventBus();
  const templateVariables = useTemplateVariableStore.getState();
  const {
    chatMessageDisplayConfig,
    useMarkdownDisplay,
    updateMarkdownConfig,
    toggleChatMessageDisplayType,
  } = useConfigureChatMessageDisplay();

  const step = useMemo(() => {
    const patchedStep = _.cloneDeep(_step);
    patchedStep.params.fields = patchedStep.params.fields.map(field => ({
      ...field,
      disable_pasting:
        (taskContext.disablePastingTextCollectionFieldIds ?? []).includes(field.field_id) ||
        taskContext.disablePastingAllTextCollectionFields,
    }));
    return patchedStep;
  }, [
    _step,
    taskContext.disablePastingTextCollectionFieldIds,
    taskContext.disablePastingAllTextCollectionFields,
  ]);

  const config = useChatStoreExtract(state => state.config);

  const canExpandContent =
    useUnleashFeatureFlag(
      'chat-enable-expanded-model-response',
      { projectId: taskContext.projectId },
      false,
    ) ?? false;

  // Feature flags for enabling feedback display
  const enableFeedbackDisplay = useUnleashFeatureFlag(
    'enable-feedback-lazarus',
    { projectId: taskContext.projectId },
    false,
  );

  const cardRef = useRef<HTMLDivElement>(null);

  const [submitCodeErrors, setSubmitCodeErrors] = useState<Record<string, string>>({});
  const setSubmitCodeError = useCallback(
    (field_id: string, err: string) => {
      setSubmitCodeErrors(prevErrors => ({ ...prevErrors, [field_id]: err }));
    },
    [setSubmitCodeErrors],
  );
  const submitCodeError = useMemo(() => {
    return Object.values(submitCodeErrors).find(error => error.length > 0);
  }, [submitCodeErrors]);

  const [currentResponse, setCurrentResponse] = useState<TextCollectionResponse>(response);

  const handleResponseChange = useCallback(
    (newResponse: TextCollectionResponse) => {
      setCurrentResponse(newResponse);
    },
    [currentResponse],
  );

  const autoCritiqueConfig = taskContext.autoCritiqueConfig?.textCollectionPerResponse;
  const autoCritiqueLabel = autoCritiqueConfig?.renameButton;

  const autoCritiqueHook = useAutoCritique();
  const fetchAutoCritique = useCallback(() => {
    if (lastPrompt) {
      autoCritiqueHook.getAutoCritique({
        scope: 'textCollectionPerResponse',
        projectId: taskContext.projectId,
        taskId: taskContext.taskId,
        assignmentId: taskContext.assignmentId,
        workerId: taskContext.workerId,
        variables: {
          ...config.templateVariables,
          ...extractVariablesFromTextCollectionResponse(currentResponse),
          prompt: lastPrompt ?? '',
          response: lastResponse ?? '',
          candidateText,
        },
      });
    }
  }, [
    lastPrompt,
    autoCritiqueHook,
    taskContext.projectId,
    taskContext.taskId,
    taskContext.assignmentId,
    taskContext.workerId,
    config.templateVariables,
    currentResponse,
    lastResponse,
    candidateText,
  ]);

  const [codeGenerationEnforcementPassed, setCodeGenerationEnforcementPassed] =
    useState<boolean>(true);

  const resultChanged = useMemo(
    () =>
      !_.isEqual(response?.annotations, currentResponse?.annotations) ||
      !_.isEqual(reactUIGeneratedUrlWithMetadata, savedReactUIGeneratedUrlWithMetadata),
    [
      response,
      currentResponse,
      reactUIGeneratedUrlWithMetadata,
      savedReactUIGeneratedUrlWithMetadata,
    ],
  );
  const hasUnsavedChanges = hasSubmittedResponse && resultChanged;

  const { canSubmit, submitTooltip } = useCandidateTextCollectionValidation({
    enableFeedbackDisplay: enableFeedbackDisplay ?? false,
    readOnly,
    resultChanged,
    submitCodeError,
    response: currentResponse,
    fields: step.params.fields,
    templateVariables,
    allAttachmentsViewed: true,
    autoCritiqueIsLoading: autoCritiqueHook.critiqueData.loading,
    codeGenerationEnforcementPassed,
    feedback,
  });

  const preview =
    canExpandContent && candidatePreviewText ? (
      <CandidatePreviewContent
        canExpandContent={canExpandContent}
        previewText={candidatePreviewText}
        fontSizeClass={fontSizeClass}
        enableRTL={_step.params.enable_rtl_text}
        useMarkdownDisplay={useMarkdownDisplay}
        chatMessageDisplayConfig={chatMessageDisplayConfig}
        taskContext={taskContext}
      />
    ) : undefined;

  /*
   * Mark the candidate's completed state based on the local changes
   * Only relevant if the step has already been submitted at least once
   */
  useEffect(() => {
    if (hasSubmittedResponse) {
      setCandidateCompletedState(candidateId, !resultChanged);
    }
  }, [hasSubmittedResponse, candidateId, resultChanged, setCandidateCompletedState]);

  return (
    <CardAlert message={hasUnsavedChanges ? 'Please save the section below.' : undefined}>
      <StepCard
        className={twMerge(clsx(!hasUnsavedChanges && 'rounded-none border-0 border-t', className))}
        readOnly={readOnly}
      >
        <StepCard.SubHeader className={clsx(hasUnsavedChanges && 'rounded-t-lg')}>
          <StepCard.HeaderLeft>
            <StepCard.SubHeaderTitle>{name}</StepCard.SubHeaderTitle>
          </StepCard.HeaderLeft>
          <StepCard.HeaderRight>
            {enableIndividualModelResponseReroll &&
              !_.isNil(previousModelResponseSelectorStepId) && (
                <Icon
                  className="text-neutral-500"
                  variant="neutral"
                  size="sm"
                  icon="arrow-rotate-left"
                  tooltip="Fetch new response"
                  tooltipProps={{
                    delayDuration: 0,
                  }}
                  onClick={() => {
                    eventBus.emit('chat:reroll-individual-model-response', {
                      candidateId,
                      previousModelResponseSelectorStepId,
                    });
                  }}
                />
              )}
            {taskContext.enableFontSizeSelector && <FontSizeSelector onChange={setFontSizeClass} />}
            <ChatMessageDisplayConfigButton
              chatMessageDisplayConfig={chatMessageDisplayConfig}
              useMarkdownDisplay={useMarkdownDisplay}
              updateMarkdownConfig={updateMarkdownConfig}
              toggleChatMessageDisplayType={toggleChatMessageDisplayType}
            />
          </StepCard.HeaderRight>
        </StepCard.SubHeader>
        <StepCard.Body>
          <div ref={cardRef}>
            <ResizePanel
              className="my-2"
              left={
                <UtilitiesWrapper
                  className="h-full"
                  enableRTL={_step.params.enable_rtl_text}
                  disableFlex={true}
                >
                  <div className="flex flex-col h-full">
                    <div className="flex-grow">
                      <CandidateRenderedContent
                        canExpandContent={canExpandContent}
                        preview={preview}
                        candidateText={candidateText}
                        fontSizeClass={fontSizeClass}
                        enableRTL={_step.params.enable_rtl_text}
                        renderCode={config?.renderCode}
                        useMarkdownDisplay={useMarkdownDisplay}
                        chatMessageDisplayConfig={chatMessageDisplayConfig}
                        renderUIRendering={_step.params.enable_ui_generation}
                        extractTextRegex={_step.params.extract_model_response_regex}
                        taskContext={taskContext}
                        setCodeGenerationEnforcementPassed={setCodeGenerationEnforcementPassed}
                        enforcementMode={_step.params.ui_generation_enforcement_mode}
                        reactUIGeneratedUrlWithMetadata={reactUIGeneratedUrlWithMetadata}
                        setReactUIGeneratedUrlWithMetadata={setReactUIGeneratedUrlWithMetadata}
                        uid={candidateId}
                      />
                      <AttachmentViewer attachments={attachments ?? []} />
                    </div>
                    <FooterContainer className="flex-grow-0">
                      {taskContext.enableWordCounter && (
                        <WordCounter text={candidateText} active={false} />
                      )}
                    </FooterContainer>
                  </div>
                </UtilitiesWrapper>
              }
              right={
                <ChatStepChildIdContext.Provider
                  value={{
                    childId: responseTemplatingProps.showResponseTemplatingIcon
                      ? responseTemplatingProps.childId
                      : undefined,
                    metadata: responseTemplatingProps.metadata,
                  }}
                >
                  <SimpleTextCollectionCore
                    params={{
                      attachments: [],
                      fields: step.params.fields,
                      enable_rtl_text: step.params.enable_rtl_text,
                    }}
                    response={currentResponse}
                    setResponse={handleResponseChange}
                    readOnly={readOnly}
                    chatTaskBehaviorLogMetadata={chatTaskBehaviorLogMetadata}
                    assignmentId={taskContext.assignmentId}
                    setSubmitCodeError={setSubmitCodeError}
                    readOnlyMessage="This section is read-only."
                    codingUnitTestsGenerationConfig={taskContext.enableCodingUnitTestsGeneration}
                    projectId={taskContext.projectId}
                    lastPrompt={lastPrompt}
                  />
                </ChatStepChildIdContext.Provider>
              }
            />

            {enableFeedbackDisplay && feedback && feedback.length > 0 && (
              <div className="mt-4">
                {feedback.map((item, index) => (
                  <FeedbackCard
                    key={index}
                    feedback={item}
                    index={index}
                    response={currentResponse}
                    setResponse={setCurrentResponse}
                    readOnly={readOnly}
                    chatMessageDisplayConfig={chatMessageDisplayConfig}
                    taskContext={taskContext}
                    enableRTL={_step.params.enable_rtl_text}
                  />
                ))}
              </div>
            )}

            <AutoCritiqueAlert
              autoCritiqueHook={autoCritiqueHook}
              headerLabel={autoCritiqueLabel}
            />
          </div>
        </StepCard.Body>
        <StepCard.Footer className="flex gap-2 justify-end">
          {autoCritiqueConfig?.enabled && (
            <AutoCritiqueButton
              className="ml-auto"
              onClick={fetchAutoCritique}
              loading={autoCritiqueHook.critiqueData.loading}
              buttonLabel={autoCritiqueLabel}
            />
          )}

          {!taskContext.previewMode && (
            <Tooltip
              text={submitTooltip}
              side="top"
              disabled={canSubmit}
              target={
                <div>
                  <Button
                    onClick={async () => {
                      onResponseSubmit(currentResponse);
                    }}
                    disabled={!canSubmit}
                    data-testid="candidate-text-collection-submit"
                  >
                    {'Save Changes'}
                  </Button>
                </div>
              }
            />
          )}
        </StepCard.Footer>
      </StepCard>
    </CardAlert>
  );
};

const CandidatePreviewContent = ({
  canExpandContent,
  fontSizeClass,
  enableRTL,
  previewText,
  useMarkdownDisplay,
  chatMessageDisplayConfig,
  taskContext,
}: {
  canExpandContent: boolean;
  previewText: string;
  fontSizeClass: string;
  enableRTL: boolean | undefined;
  useMarkdownDisplay: boolean;
  chatMessageDisplayConfig: any;
  taskContext: ChatTaskContextType;
}) => {
  if (!canExpandContent) {
    return null;
  }

  if (useMarkdownDisplay) {
    return (
      <ChatMarkdown
        className={clsx('border-none p-1', fontSizeClass)}
        markdown={previewText}
        enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
        enableSingleDollarTextMath={
          chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
        }
        enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
        enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
        enableExecutableCodeBlocks={
          taskContext.codingConfig?.enableExecutableCodeMarkdown ??
          taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
        }
        enableRTL={enableRTL}
      />
    );
  } else {
    return <div className="text-neutral-900 text-sm whitespace-pre-wrap">{previewText}</div>;
  }
};

const CandidateRenderedContent = ({
  canExpandContent,
  preview,
  candidateText,
  fontSizeClass,
  enableRTL,
  renderCode,
  useMarkdownDisplay,
  chatMessageDisplayConfig,
  renderUIRendering,
  extractTextRegex,
  taskContext,
  setCodeGenerationEnforcementPassed,
  enforcementMode,
  reactUIGeneratedUrlWithMetadata,
  setReactUIGeneratedUrlWithMetadata,
  uid,
}: {
  canExpandContent: boolean;
  preview?: ReactNode;
  candidateText: string;
  fontSizeClass: string;
  enableRTL: boolean | undefined;
  renderCode: boolean | undefined;
  renderUIRendering: boolean | undefined;
  useMarkdownDisplay: boolean;
  chatMessageDisplayConfig: any;
  extractTextRegex?: string;
  taskContext: ChatTaskContextType;
  enforcementMode?: UIGenerationEnforcementMode;
  setCodeGenerationEnforcementPassed: (passed: boolean) => void;
  reactUIGeneratedUrlWithMetadata?: {
    reactUIGeneratedUrl?: string;
  } & UIGenerationMetadata;
  setReactUIGeneratedUrlWithMetadata?: (
    urlWithMetadata: {
      reactUIGeneratedUrl?: string;
    } & UIGenerationMetadata,
  ) => void;
  uid: string;
}) => {
  let fullContent;
  const extractedMarkdownText = extractTextRegex
    ? extractTextFromRegex(candidateText, extractTextRegex)
    : candidateText;
  if (useMarkdownDisplay) {
    fullContent = (
      <>
        {renderCode && <ChatIFrame src={candidateText} />}
        <ChatMarkdown
          className={clsx('border-none p-1', fontSizeClass)}
          markdown={extractedMarkdownText}
          enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
          enableSingleDollarTextMath={
            chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
          }
          enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
          enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
          enableExecutableCodeBlocks={
            taskContext.codingConfig?.enableExecutableCodeMarkdown ??
            taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
          }
          enableRTL={enableRTL}
        />
        {renderUIRendering && (
          <ChatHtmlRenderer
            source="tcpr"
            src={candidateText}
            enforcementMode={enforcementMode}
            setEnforcementPassed={setCodeGenerationEnforcementPassed}
            contentUrlWithMetadata={reactUIGeneratedUrlWithMetadata}
            setContentGeneratedUrlWithMetadata={setReactUIGeneratedUrlWithMetadata}
            uid={uid}
          />
        )}
      </>
    );
  } else {
    fullContent = (
      <div className="text-neutral-900 text-sm whitespace-pre-wrap">{extractedMarkdownText}</div>
    );
  }

  if (canExpandContent) {
    return (
      <ExpandableContent preview={preview} expandButtonText="Show full response">
        {fullContent}
      </ExpandableContent>
    );
  }

  return fullContent;
};

export default memo(CandidateTextCollectionCard);
